import { useId, useState } from "react";
import type { DragEvent } from "react";

import { formatBytes } from "../lib/format";
import { Icon } from "./Icon";

interface DropzoneProps {
  label: string;
  description: string;
  file: File | null;
  error?: string | null;
  disabled?: boolean;
  onFile: (file: File | null) => void;
}

export function Dropzone({ label, description, file, error, disabled, onFile }: DropzoneProps) {
  const inputId = useId();
  const [dragging, setDragging] = useState(false);

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files[0];
    if (dropped) onFile(dropped);
  }

  const state = error ? " dropzone--error" : file ? " dropzone--filled" : "";
  return (
    <div className="dropzone-field">
      <span className="field-label" id={`${inputId}-label`}>
        {label}
      </span>
      <label
        htmlFor={inputId}
        className={`dropzone${dragging ? " dropzone--dragging" : ""}${state}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input
          id={inputId}
          type="file"
          className="visually-hidden"
          accept=".xlsx,.csv"
          disabled={disabled}
          aria-labelledby={`${inputId}-label`}
          aria-describedby={`${inputId}-hint`}
          onChange={(event) => onFile(event.target.files?.[0] ?? null)}
        />
        <Icon name={file ? "file" : "upload"} size={22} />
        {file ? (
          <span className="dropzone__file">
            <strong>{file.name}</strong>
            <span>{formatBytes(file.size)} · click to replace</span>
          </span>
        ) : (
          <span className="dropzone__prompt">
            <strong>Drop XLSX or CSV</strong>
            <span>or click to browse</span>
          </span>
        )}
      </label>
      <p className="field-hint" id={`${inputId}-hint`}>
        {description}
      </p>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
